import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "gatsby";
import allMenus from "../data/menu.data";

function TopMenus() {
  const [openDesktop, setOpenDesktop] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const closeTimer = useRef(null);
  const navRef = useRef(null);

  // Desktop hover handlers with delay
  const handleMouseEnter = useCallback((index) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDesktop(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenDesktop(null), 300);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenDesktop(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close mobile on route change (link click)
  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileAccordion(null);
  }, []);

  // Toggle mobile accordion (only one open at a time)
  const toggleAccordion = useCallback((index) => {
    setMobileAccordion((prev) => (prev === index ? null : index));
  }, []);

  // Determine if a link is external
  const isExternal = (link) => link && link.startsWith("http");

  // Render a nav link (internal or external)
  const renderLink = (name, link, className, onClick, key) => {
    if (!link || link === "#") {
      return (
        <span className={className} key={key}>
          {name}
        </span>
      );
    }
    if (isExternal(link)) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={onClick}
          key={key}
        >
          {name}
        </a>
      );
    }
    return (
      <Link to={link} className={className} onClick={onClick} key={key}>
        {name}
      </Link>
    );
  };

  return (
    <>
      {/* ===== DESKTOP MEGA OVERLAY ===== */}
      <div
        className={`pz-nav__mega-dim${
          openDesktop !== null && allMenus[openDesktop]?.type === "mega"
            ? " pz-nav__mega-dim--visible"
            : ""
        }`}
      />

      {/* ===== DESKTOP NAVIGATION ===== */}
      <ul className="pz-nav__menu" ref={navRef}>
        {allMenus.map((menu, i) => (
          <li
            key={menu.name}
            className={`pz-nav__item${menu.type === "mega" ? " pz-nav__item--mega" : ""}${openDesktop === i ? " pz-nav__item--open" : ""}`}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="pz-nav__link"
              aria-expanded={openDesktop === i}
              aria-haspopup="true"
              onClick={() => setOpenDesktop(openDesktop === i ? null : i)}
            >
              {menu.name}
              <i className="mdi mdi-chevron-down pz-nav__chevron" />
            </button>

            {/* Mega Menu */}
            {menu.type === "mega" && (
              <div className="pz-nav__mega" role="menu">
                <div className="pz-nav__mega-inner">
                  <div
                    className={`pz-nav__mega-grid${menu.featured ? " pz-nav__mega-grid--featured" : ""}`}
                  >
                    {menu.columns.map((col) => (
                      <div key={col.heading} className="pz-nav__mega-col">
                        {col.link ? (
                          <Link
                            to={col.link}
                            className="pz-nav__col-heading pz-nav__col-heading--link"
                            onClick={() => setOpenDesktop(null)}
                          >
                            {col.heading}
                          </Link>
                        ) : (
                          <div className="pz-nav__col-heading">{col.heading}</div>
                        )}
                        {col.items.map((item) =>
                          item.icon
                            ? renderLink(
                                <><span className="pz-nav__col-icon"><i className={item.icon} /></span>{item.name}</>,
                                item.link,
                                "pz-nav__col-item",
                                () => setOpenDesktop(null),
                                `${col.heading}-${item.name}`
                              )
                            : renderLink(
                                item.name,
                                item.link,
                                "pz-nav__col-item",
                                () => setOpenDesktop(null),
                                `${col.heading}-${item.name}`
                              )
                        )}
                      </div>
                    ))}
                    {menu.featured && (
                      <div className="pz-nav__featured">
                        <div className="pz-nav__featured-title">
                          {menu.featured.title}
                        </div>
                        <div className="pz-nav__featured-desc">
                          {menu.featured.desc}
                        </div>
                        <Link
                          to={menu.featured.link}
                          className="pz-nav__featured-cta"
                          onClick={() => setOpenDesktop(null)}
                        >
                          {menu.featured.cta} &rarr;
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Simple Dropdown */}
            {menu.type === "simple" && (
              <div className="pz-nav__dropdown" role="menu">
                {menu.items.map((item) =>
                  renderLink(
                    item.name,
                    item.link,
                    "pz-nav__drop-item",
                    () => setOpenDesktop(null),
                    `${menu.name}-${item.name}`
                  )
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ===== MOBILE HAMBURGER ===== */}
      <button
        className={`pz-nav__hamburger${mobileOpen ? " pz-nav__hamburger--open" : ""}`}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileOpen}
      >
        <span />
        <span />
        <span />
      </button>

      {/* ===== MOBILE BACKDROP ===== */}
      <div
        className={`pz-nav__backdrop${mobileOpen ? " pz-nav__backdrop--visible" : ""}`}
        onClick={closeMobile}
      />

      {/* ===== MOBILE OVERLAY ===== */}
      <div
        className={`pz-nav__mobile${mobileOpen ? " pz-nav__mobile--open" : ""}`}
      >
        <button
          className="pz-nav__mobile-close"
          onClick={closeMobile}
          aria-label="Close menu"
        >
          <i className="mdi mdi-close" />
        </button>

        {allMenus.map((menu, i) => (
          <div
            key={menu.name}
            className={`pz-nav__mobile-item${mobileAccordion === i ? " pz-nav__mobile-item--open" : ""}`}
          >
            <button
              className="pz-nav__mobile-trigger"
              onClick={() => toggleAccordion(i)}
              aria-expanded={mobileAccordion === i}
            >
              {menu.name}
              <i className="mdi mdi-chevron-down pz-nav__chevron" />
            </button>

            {mobileAccordion === i && (
              <div className="pz-nav__mobile-panel">
                {menu.type === "mega" &&
                  menu.columns.map((col) => (
                    <React.Fragment key={col.heading}>
                      {col.link ? (
                        <Link
                          to={col.link}
                          className="pz-nav__mobile-heading pz-nav__mobile-heading--link"
                          onClick={closeMobile}
                        >
                          {col.heading}
                        </Link>
                      ) : (
                        <div className="pz-nav__mobile-heading">
                          {col.heading}
                        </div>
                      )}
                      {col.items.map((item) =>
                        renderLink(
                          item.name,
                          item.link,
                          "pz-nav__mobile-link",
                          closeMobile,
                          `mobile-${col.heading}-${item.name}`
                        )
                      )}
                    </React.Fragment>
                  ))}
                {menu.type === "simple" &&
                  menu.items.map((item) =>
                    renderLink(
                      item.name,
                      item.link,
                      "pz-nav__mobile-link",
                      closeMobile,
                      `mobile-${menu.name}-${item.name}`
                    )
                  )}
              </div>
            )}
          </div>
        ))}

        {/* Mobile CTA */}
        <button
          className="pz-nav__mobile-cta"
          data-toggle="modal"
          data-target="#contact"
          onClick={closeMobile}
        >
          Say Hello <i className="mdi mdi-arrow-right" />
        </button>
      </div>
    </>
  );
}

export default TopMenus;
